import { Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { UseFormReturn } from 'react-hook-form';
import { getRangeDisplayName } from 'components/filter/input/DateRangePicker/utils/rangeDisplay';
import { DateValue } from 'components/filter/input/DateRangePicker/types';
import { FilterSetMatcher } from 'utils/filterSetMatcher';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { translateSeason } from 'utils/translateAttribute';
import { useProductDataService } from 'services/product-data.service';
import { useEffect, useState } from 'react';
import { AdvancedFilterData } from '../FilterFormController';

interface SelectedFiltersTableProps {
  formContext: UseFormReturn<AdvancedFilterData>;
}

export function FormFilterList({ formContext }: SelectedFiltersTableProps) {
  const intl = useIntl();
  const { fetchProductsByNumbers } = useProductDataService();
  const [productNames, setProductNames] = useState<Record<string, string>>({});

  // Get all available options from the same store used by Select components
  const { allBrands, allMarketplaces, allCategories, allGenders, allAgeGroups, allSeasons, allCountries } =
    useTransitiveCacheStore();

  // Get only the filters that differ from defaults using the established logic
  const currentValues = formContext.getValues();
  const changedFilters = FilterSetMatcher.extractChangedFilters(currentValues);

  // Fetch product names when selectedProducts change
  useEffect(() => {
    const productIds = changedFilters.selectedProducts;
    if (productIds && productIds.length > 0) {
      fetchProductsByNumbers(productIds).then(products => {
        const nameMap: Record<string, string> = {};
        products.forEach(product => {
          nameMap[product.number] = product.name;
        });
        setProductNames(nameMap);
      });
    } else {
      setProductNames({});
    }
  }, [changedFilters.selectedProducts, fetchProductsByNumbers]);

  // Helper function to convert IDs to labels using the same logic as Select components
  const getLabelsForIds = (key: keyof AdvancedFilterData, ids: string[]): string[] => {
    switch (key) {
      case 'selectedBrands':
        return ids.map(id => {
          const brand = allBrands.find(b => b.id === id);
          return brand?.label ?? id;
        });

      case 'selectedMarketplaces':
        return ids.map(id => {
          const marketplace = allMarketplaces.find(m => m.name === id);
          return marketplace?.name ?? id;
        });

      case 'selectedCategories':
        return ids.map(id => {
          const category = allCategories.find(c => c.categoryCode === id);
          return category
            ? intl.formatMessage({
                id: `product.categories.${category.categoryCode}`,
                defaultMessage: category.categoryCode,
              })
            : id;
        });

      case 'selectedGenders':
        return ids.map(id => {
          const gender = allGenders.find(g => g.genderCode === id);
          return gender
            ? intl.formatMessage({
                id: `product.genders.${gender.genderCode}`,
                defaultMessage: gender.genderCode,
              })
            : id;
        });

      case 'selectedAgeGroups':
        return ids.map(id => {
          const ageGroup = allAgeGroups.find(a => a.ageGroup === id);
          return ageGroup
            ? intl.formatMessage({
                id: `product.ageGroup.${ageGroup.ageGroup}`,
                defaultMessage: ageGroup.ageGroup,
              })
            : id;
        });

      case 'selectedSeasons':
        return ids.map(id => {
          const season = allSeasons.find(s => s.seasonCode === id);
          return season ? translateSeason(season.seasonCode, intl) : id;
        });

      case 'selectedFulfillmentMethods':
        // For fulfillment methods, format the ID directly since they're typically readable
        return ids.map(id => id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));

      case 'selectedProducts':
        // Use the fetched product names
        return ids.map(id => productNames[id] || id);

      case 'selectedCountries':
        return ids.map(id => {
          const isGlobalRegion = (countryCode: string) => countryCode.toLowerCase() === 'zz';

          if (isGlobalRegion(id)) {
            return intl.formatMessage({ id: 'filters.global', defaultMessage: 'Global' });
          }

          const country = allCountries.find(c => c.countryCode === id);
          if (country) {
            return (
              intl.formatDisplayName(country.countryCode.toUpperCase(), {
                type: 'region',
              }) ?? id
            );
          }

          return id;
        });

      case 'selectedColors':
        // Colors are self-descriptive strings, return as-is
        return ids;

      case 'selectedSizes':
        // Sizes are self-descriptive strings, return as-is
        return ids;

      default:
        return ids;
    }
  };

  // Format different value types using existing project methods
  const formatValue = (key: keyof AdvancedFilterData, val: unknown): string => {
    if (Array.isArray(val)) {
      // Convert IDs to labels for array values - only for string arrays
      if (val.every(item => typeof item === 'string')) {
        const labels = getLabelsForIds(key, val);
        return labels.join(', ');
      }
      return val.join(', ');
    }

    // Handle DateValue objects using the native getRangeDisplayName function
    if (
      (key === 'selectedDateRange' || key === 'selectedCreationDateRange') &&
      typeof val === 'object' &&
      val !== null
    ) {
      const dateValue = val as DateValue;
      const parts: string[] = [];

      // Use the existing getRangeDisplayName function from the project
      if (dateValue.range) {
        const mainRange = getRangeDisplayName(
          dateValue.range,
          {
            defaultValue: 'datepicker.empty.default',
          },
          false, // Don't force dates - prefer preset labels
          intl
        );
        parts.push(mainRange);
      }

      // Add comparison range if it exists and is not empty/default
      if (dateValue.comparison) {
        const comparisonRange = getRangeDisplayName(
          dateValue.comparison,
          {
            defaultValue: 'datepicker.empty.default',
          },
          false, // Don't force dates - prefer preset labels
          intl
        );

        // Only add comparison if it's not the default "Select date" text
        const defaultText = intl.formatMessage({
          id: 'datepicker.empty.default',
          defaultMessage: 'Select date',
        });

        if (comparisonRange && comparisonRange !== defaultText) {
          const comparisonLabel = intl.formatMessage(
            {
              id: 'measurecomparison.period',
              defaultMessage: 'compared to {timePeriode}',
            },
            { timePeriode: comparisonRange }
          );
          parts.push(comparisonLabel);
        }
      }

      return parts.join(' ');
    }

    // Handle benchmark selection
    if (key === 'selectedBenchmark' && typeof val === 'string') {
      return intl.formatMessage({
        id: `benchmarking.benchmarks.${val}`,
        defaultMessage: val,
      });
    }

    // Handle country codes is now handled in getLabelsForIds function above

    if (typeof val === 'object' && val !== null) {
      // Handle preset values with simple string conversion
      if ('presetValue' in val) {
        const presetValue = String(val.presetValue);
        return presetValue.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }

      return JSON.stringify(val);
    }

    // Handle Date objects
    if (val instanceof Date) {
      return val.toLocaleDateString(intl.locale);
    }

    return String(val);
  };

  return (
    <div className="flex flex-col">
      <Typography variant="h4" sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <FormattedMessage id="advanced-filter-set-selectedFilters" defaultMessage="Selected filters" />
      </Typography>
      <div className="mt-4">
        {Object.keys(changedFilters).length === 0 ? (
          <p className="text-sm text-gray-500">
            <FormattedMessage
              id="advanced-filter-no-filters-selected"
              defaultMessage="No filters selected (all filters are at default values)"
            />
          </p>
        ) : (
          <table className="">
            <tbody>
              {Object.entries(changedFilters).map(([key, value]) => (
                <tr key={key}>
                  <td className="font-medium text-sm py-2 pr-8 align-top">
                    {intl.formatMessage({
                      id: `filter.key.${key}`,
                      defaultMessage: key,
                    })}
                  </td>
                  <td className="text-sm text-gray-600 py-2 align-top">
                    {formatValue(key as keyof AdvancedFilterData, value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
